'use client';

import { useState } from 'react';
import { 
  ChartBarIcon, 
  UsersIcon, 
  CogIcon, 
  BellIcon,
  ArrowTrendingUpIcon,
  CurrencyDollarIcon,
  HomeIcon,
  UserGroupIcon,
  DocumentTextIcon,
  ChartPieIcon,
  Cog6ToothIcon,
  Bars3Icon,
  XMarkIcon,
  MapIcon
} from '@heroicons/react/24/outline';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  change?: string;
}

const StatCard = ({ title, value, icon, change }: StatCardProps) => (
  <div className="bg-white rounded-lg shadow p-6">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600">{title}</p>
        <p className="text-2xl font-semibold text-gray-900 mt-1">{value}</p>
        {change && (
          <p className="text-sm text-green-600 mt-1">{change}</p>
        )}
      </div>
      <div className="p-3 bg-blue-50 rounded-full">
        {icon}
      </div>
    </div>
  </div>
);

interface NavItemProps {
  icon: React.ReactNode;
  label: string;
  active?: boolean;
}

const NavItem = ({ icon, label, active }: NavItemProps) => (
  <a
    href="#"
    className={`flex items-center px-4 py-2 text-sm font-medium rounded-md ${
      active
        ? 'bg-gray-100 text-gray-900'
        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
    }`}
  >
    <div className="mr-3">{icon}</div>
    {label}
  </a>
);

export default function AdminDashboard() {
  const [sidebarOpen, setSidebarOpen] = useState(true);

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex items-center justify-between h-16 px-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">MonitLog</h2>
          <button
            onClick={() => setSidebarOpen(false)}
            className="p-2 rounded-md text-gray-400 hover:text-gray-500 lg:hidden"
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>
        <nav className="px-2 py-4 space-y-1">
          <NavItem icon={<HomeIcon className="h-5 w-5" />} label="Dashboard" active />
          <NavItem icon={<UsersIcon className="h-5 w-5" />} label="Users" />
          <NavItem icon={<UserGroupIcon className="h-5 w-5" />} label="Teams" />
          <NavItem icon={<MapIcon className="h-5 w-5" />} label="States" />
          <NavItem icon={<DocumentTextIcon className="h-5 w-5" />} label="Reports" />
          <NavItem icon={<ChartPieIcon className="h-5 w-5" />} label="Analytics" />
          <NavItem icon={<Cog6ToothIcon className="h-5 w-5" />} label="Settings" />
        </nav>
      </div>

      {/* Main Content */}
      <div className={`${sidebarOpen ? 'lg:pl-64' : ''} min-h-screen`}>
        {/* Top Navigation */}
        <nav className="bg-white shadow-sm">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center">
                <button
                  onClick={() => setSidebarOpen(true)}
                  className="p-2 rounded-md text-gray-400 hover:text-gray-500 lg:hidden"
                >
                  <Bars3Icon className="h-6 w-6" />
                </button>
                <h1 className="text-xl font-semibold text-gray-900 ml-4">Admin Dashboard</h1>
              </div>
              <div className="flex items-center space-x-4">
                <button className="p-2 text-gray-400 hover:text-gray-500">
                  <BellIcon className="h-6 w-6" />
                </button>
                <button className="p-2 text-gray-400 hover:text-gray-500">
                  <CogIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>
        </nav>

        {/* Main Content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8">
          {/* Stats Grid */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              title="Total Users"
              value="1,234"
              icon={<UsersIcon className="h-6 w-6 text-blue-600" />}
              change="+12% from last month"
            />
            <StatCard
              title="Revenue"
              value="$12,345"
              icon={<CurrencyDollarIcon className="h-6 w-6 text-green-600" />}
              change="+8% from last month"
            />
            <StatCard
              title="Active Sessions"
              value="456"
              icon={<ChartBarIcon className="h-6 w-6 text-purple-600" />}
              change="+5% from last month"
            />
            <StatCard
              title="Growth"
              value="23%"
              icon={<ArrowTrendingUpIcon className="h-6 w-6 text-orange-600" />}
              change="+2% from last month"
            />
          </div>

          {/* Main Content Area */}
          <div className="mt-8 grid grid-cols-1 gap-5 lg:grid-cols-2">
            {/* Recent Activity */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Recent Activity</h2>
              <div className="space-y-4">
                {/* Placeholder for activity items */}
                <p className="text-gray-500">Activity data will be loaded here...</p>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h2>
              <div className="grid grid-cols-2 gap-4">
                <button className="p-4 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100">
                  Add New User
                </button>
                <button className="p-4 bg-green-50 text-green-700 rounded-lg hover:bg-green-100">
                  Generate Report
                </button>
                <button className="p-4 bg-purple-50 text-purple-700 rounded-lg hover:bg-purple-100">
                  View Analytics
                </button>
                <button className="p-4 bg-orange-50 text-orange-700 rounded-lg hover:bg-orange-100">
                  Manage Settings
                </button>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  );
} 